import { Box, Card, FormControl, Grid, MenuItem, Modal, Select, TextField, Tooltip, Typography } from '@mui/material';
import CancelIcon from '../../../icons/close.svg';
import { LoadingButton } from '@mui/lab';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useEffect } from 'react';
import { AnnouncementPersonalizationDto, AnnouncementPersonalizationFields } from '@inosio/payments-sdk';
import HelpIcon from '@mui/icons-material/Help';

export interface AnnouncementPersonalizeModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  onSelectPersonalizeField?: (field: AnnouncementPersonalizationDto) => void;
  defaultPeronalizeField?: AnnouncementPersonalizationDto;
}

interface CategoryField {
  value: string;
  label: string;
}

interface Category {
  value: string;
  label: string;
  fields: CategoryField[];
}

const schemaValidationPersonalize = Yup.object({
  category: Yup.string().required('Select Category'),
  field: Yup.string().required('Select Field'),
  fallbackValue: Yup.string()
    .required('Fallback value is required')
    .matches(/^[a-zA-Z0-9\s]*$/, 'Fallback value must be alphanumeric and can contain spaces, but no special characters'),
});

type schemaValidationPersonalizeType = Yup.InferType<typeof schemaValidationPersonalize>;

const categoryOptions: Category[] = [
  {
    label: 'Company',
    value: 'company',
    fields: [
      {
        label: 'DBA Name',
        value: AnnouncementPersonalizationFields.CompanyDbaName,
      },
      {
        label: 'Legal Name',
        value: AnnouncementPersonalizationFields.CompanyLegalName,
      },
      {
        label: 'Mailing Address',
        value: AnnouncementPersonalizationFields.CompanyMailingAddress,
      },
      {
        label: 'Physical Address',
        value: AnnouncementPersonalizationFields.CompanyPhysicalAddress,
      },
    ],
  },
  {
    label: 'Property',
    value: 'property',
    fields: [
      {
        label: 'Property Name',
        value: AnnouncementPersonalizationFields.PropertyName,
      },
      {
        label: 'Property Full Address',
        value: AnnouncementPersonalizationFields.PropertyFullAddress,
      },
    ],
  },
  {
    label: 'Recipient',
    value: 'recipient',
    fields: [
      {
        label: 'First Name',
        value: AnnouncementPersonalizationFields.RecipientFirstName,
      },
      {
        label: 'Full Name',
        value: AnnouncementPersonalizationFields.RecipientFullname,
      },
    ],
  },
];

export const AnnouncementPersonalizeModal = (props: AnnouncementPersonalizeModalProps) => {
  const formik = useFormik<schemaValidationPersonalizeType>({
    initialValues: {
      category: '',
      field: '',
      fallbackValue: '',
    },
    validationSchema: schemaValidationPersonalize,

    onSubmit: async (values) => {
      if (props.onSelectPersonalizeField) {
        props.onSelectPersonalizeField({
          Key: values.field as AnnouncementPersonalizationFields,
          FallbackValue: values.fallbackValue,
        });
        formik.resetForm();
      }
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '16px',
    height: '523px',
    pointerEvents: 'auto',
    width: '572px',
    maxWidth: '572px',
    borderRadius: '24px',
    outline: 'none',
  };

  const getError = (fieldName: keyof schemaValidationPersonalizeType) => {
    return formik.touched[fieldName] && formik.errors[fieldName] ? <Typography sx={{ fontSize: '12px', color: '#d32f2f', fontFamily: 'Work Sans' }}>{formik.errors[fieldName]}</Typography> : <></>;
  };

  useEffect(() => {
    if (props.open && props.defaultPeronalizeField) {
      const category = categoryOptions.find((category) => category.fields.some((field) => field.value === props.defaultPeronalizeField?.Key));

      if (category) {
        formik.setFieldValue('category', category.value);
        formik.setFieldValue('field', props.defaultPeronalizeField.Key);
        formik.setFieldValue('fallbackValue', props.defaultPeronalizeField.FallbackValue);
      }
    }
  }, [props.open, props.defaultPeronalizeField]);

  const onHandleClose = () => {
    formik.resetForm();
    if (props.onClose) {
      props.onClose();
    }
  };

  return (
    <div>
      <Modal
        open={props.open}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        BackdropProps={{
          invisible: false,
        }}
        disableEscapeKeyDown
        disableAutoFocus={true}
      >
        <Card sx={style}>
          <Grid container display="flex" justifyContent="center">
            <Grid item xs={12} display="flex" justifyContent="center">
              <Typography
                sx={{
                  fontFamily: 'Work Sans !important',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  fontSize: '18px !important',
                  lineHeight: '21px !important',
                  color: '#14142B',
                }}
              >
                {props.title || 'Personalize'}
              </Typography>
            </Grid>

            <Grid container mt={'30px'} rowSpacing={2} sx={{ width: '100%', height: '100%'  , padding: '0px  20px'}}>
              <Grid item xs={12} sm={3.5} sx={{ display: 'flex', alignItems: 'center' , height: 55}}>
                <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Category:</Typography>
              </Grid>
              <Grid item xs={12} sm={8.5}>
                <FormControl sx={{ width: '100%', borderRadius: 24, height: 55 }} size="small">
                  <Select
                    sx={{ borderRadius: '24px', height: '42px' }}
                    error={!!formik.errors.category && formik.touched.category}
                    {...formik.getFieldProps('category')}
                    fullWidth
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '24px',
                          marginTop: '10px',
                        },
                      },
                    }}
                  >
                    <MenuItem disabled value="">
                      Select Category
                    </MenuItem>
                    {categoryOptions.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {getError('category')}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3.5} sx={{ display: 'flex', alignItems: 'center', height: 55 }}>
                <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Variable:</Typography>
              </Grid>
              <Grid item xs={12} sm={8.5}>
                <FormControl sx={{ width: '100%', borderRadius: 24, height: 55 }} size="small">
                  <Select
                    sx={{ borderRadius: '24px', height: '42px' }}
                    error={!!formik.errors.field && formik.touched.field}
                    fullWidth
                    {...formik.getFieldProps('field')}
                    displayEmpty
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '24px',
                          marginTop: '10px',
                        },
                      },
                    }}
                  >
                    <MenuItem disabled value="" selected>
                      Select Field
                    </MenuItem>
                    {categoryOptions
                      ?.find((category) => category.value === formik.values.category)
                      ?.fields?.map((field) => (
                        <MenuItem key={field.value} value={field.value}>
                          {field.label}
                        </MenuItem>
                      ))}
                  </Select>
                  {getError('field')}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3.5} sx={{ display: 'flex', alignItems: 'center' , height: 55 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '7px' }}>
                  <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Fallback Value:</Typography>
                  <Tooltip title="A fallback value will be used if the selected placeholder value is empty in our system." placement="bottom">
                    <HelpIcon sx={{ fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12} sm={8.5}>
                <FormControl sx={{ width: '100%', height: 55 }} size="small">
                  <TextField
                    {...formik.getFieldProps('fallbackValue')}
                    placeholder="Fallback value"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      sx: {
                        height: '42px',
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '25px',
                      },
                    }}
                    error={!!formik.errors?.fallbackValue && formik.touched.fallbackValue}
                    helperText={formik.touched.fallbackValue && formik.errors?.fallbackValue}
                    FormHelperTextProps={{
                      sx: {
                        marginTop: '4px',
                        marginLeft: '0px',
                        fontFamily: 'Work Sans'
                      },
                    }}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ position: 'absolute', bottom: 15, width: '94%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                  variant="contained"
                  disabled={!formik.isValid}
                  style={{
                    textTransform: 'none',
                    fontFamily: "'Open Sans', sans-serif",
                    fontStyle: 'normal',
                    fontWeight: 700,
                    backgroundColor: '#39A2DB',
                    borderRadius: '24px',
                  }}
                  onClick={() => formik.handleSubmit()}
                >
                  Save
                </LoadingButton>
              </Box>
            </Box>
            {/* Close Icon */}
            <Grid item sx={{ position: 'absolute', top: 18, right: 20 }}>
              <img src={CancelIcon} alt="close" style={{ cursor: 'pointer' }} onClick={onHandleClose} />
            </Grid>
          </Grid>
        </Card>
      </Modal>
    </div>
  );
};


